// Detect international stock symbols (e.g., 000660-KO, 005930-KS, 6758-T)
export const isInternationalStock = (symbol: string): boolean => {
  return /^\d+(-[A-Z]+)?$/.test(symbol) || /^[A-Z]+-[A-Z]{2,}$/.test(symbol);
};

// Extract domain from a website URL (e.g., "https://www.skhynix.com" -> "skhynix.com")
export const extractDomain = (website?: string | null): string | null => {
  if (!website) return null;
  try {
    const url = new URL(website);
    return url.hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

interface LogoCacheEntry {
  dataUrl: string;
  timestamp: number;
  symbol: string;
}

class LogoCache {
  private memoryCache = new Map<string, string>();
  private readonly CACHE_KEY_PREFIX = 'logo_cache_';
  private readonly CACHE_DURATION = 14 * 24 * 60 * 60 * 1000; // 14 days in milliseconds
  private readonly MAX_CACHE_SIZE = 500; // Maximum number of logos to cache in localStorage

  constructor() {
    this.cleanupOldEntries();
  }

  async getLogoUrl(symbol: string, token: string, domainFallback?: string | null): Promise<string> {
    if (!symbol || !token) return '';

    const cacheKey = symbol.toUpperCase();

    // 1. Check memory cache first (fastest)
    const memoryResult = this.memoryCache.get(cacheKey);
    if (memoryResult) {
      return memoryResult;
    }

    // 2. Check localStorage (persistent but slower)
    const localStorageResult = this.getFromLocalStorage(cacheKey);
    if (localStorageResult) {
      this.memoryCache.set(cacheKey, localStorageResult);
      return localStorageResult;
    }

    // 3. For international stocks, try domain-based logo first
    if (domainFallback && isInternationalStock(symbol)) {
      try {
        const dataUrl = await this.fetchAndCacheDomain(cacheKey, domainFallback, token);
        return dataUrl;
      } catch {
        // Fall through to ticker-based lookup
      }
    }

    // 4. Fetch from API as last resort
    try {
      const dataUrl = await this.fetchAndCache(cacheKey, token);
      return dataUrl;
    } catch (error) {
      // Silently fail for 404s - many symbols don't have logos
      return '';
    }
  }

  private async fetchAndCache(symbol: string, token: string): Promise<string> {
    const apiUrl = `https://img.logo.dev/ticker/${symbol}?token=${token}`;

    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        // Don't log 404s - many symbols legitimately don't have logos
        if (response.status !== 404) {
          console.warn(`[LogoCache] Failed to fetch ${symbol}: HTTP ${response.status}`);
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();

      // Convert blob to base64 data URL
      const dataUrl = await this.blobToDataUrl(blob);

      // Cache in both memory and localStorage
      this.memoryCache.set(symbol, dataUrl);
      this.saveToLocalStorage(symbol, dataUrl);

      return dataUrl;
    } catch (error) {
      throw error;
    }
  }

  private async fetchAndCacheDomain(symbol: string, domain: string, token: string): Promise<string> {
    const apiUrl = `https://img.logo.dev/${domain}?token=${token}`;

    const response = await fetch(apiUrl);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const dataUrl = await this.blobToDataUrl(blob);

    this.memoryCache.set(symbol, dataUrl);
    this.saveToLocalStorage(symbol, dataUrl);

    return dataUrl;
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private getFromLocalStorage(symbol: string): string | null {
    try {
      const key = this.CACHE_KEY_PREFIX + symbol;
      const stored = localStorage.getItem(key);

      if (!stored) return null;

      const entry: LogoCacheEntry = JSON.parse(stored);
      const now = Date.now();

      // Check if entry is expired
      if (now - entry.timestamp > this.CACHE_DURATION) {
        localStorage.removeItem(key);
        return null;
      }

      return entry.dataUrl;
    } catch (error) {
      console.error(`[LogoCache] Error reading from localStorage:`, error);
      return null;
    }
  }

  private saveToLocalStorage(symbol: string, dataUrl: string): void {
    try {
      const key = this.CACHE_KEY_PREFIX + symbol;
      const entry: LogoCacheEntry = {
        dataUrl,
        timestamp: Date.now(),
        symbol,
      };

      localStorage.setItem(key, JSON.stringify(entry));

      // Cleanup if we're over the size limit
      this.enforceStorageLimit();
    } catch (error) {
      console.error(`[LogoCache] Error saving to localStorage:`, error);
      // If localStorage is full, try to make space
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        this.cleanupOldEntries();
        // Try one more time
        try {
          const key = this.CACHE_KEY_PREFIX + symbol;
          const entry: LogoCacheEntry = {
            dataUrl,
            timestamp: Date.now(),
            symbol,
          };
          localStorage.setItem(key, JSON.stringify(entry));
        } catch (retryError) {
          console.error(`[LogoCache] Failed to save even after cleanup:`, retryError);
        }
      }
    }
  }

  private cleanupOldEntries(): void {
    try {
      const now = Date.now();
      const keysToRemove: string[] = [];

      // Find expired entries
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_KEY_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const entry: LogoCacheEntry = JSON.parse(stored);
              if (now - entry.timestamp > this.CACHE_DURATION) {
                keysToRemove.push(key);
              }
            }
          } catch (error) {
            // If we can't parse it, remove it
            keysToRemove.push(key);
          }
        }
      }

      // Remove expired entries
      keysToRemove.forEach((key) => localStorage.removeItem(key));

      if (keysToRemove.length > 0) {
        console.log(`[LogoCache] Cleaned up ${keysToRemove.length} expired entries`);
      }
    } catch (error) {
      console.error(`[LogoCache] Error during cleanup:`, error);
    }
  }

  private enforceStorageLimit(): void {
    try {
      const logoEntries: { key: string; timestamp: number }[] = [];

      // Collect all logo entries with their timestamps
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_KEY_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const entry: LogoCacheEntry = JSON.parse(stored);
              logoEntries.push({ key, timestamp: entry.timestamp });
            }
          } catch (error) {
            // Remove corrupted entries
            localStorage.removeItem(key);
          }
        }
      }

      // If we're over the limit, remove oldest entries
      if (logoEntries.length > this.MAX_CACHE_SIZE) {
        logoEntries.sort((a, b) => a.timestamp - b.timestamp);
        const toRemove = logoEntries.slice(0, logoEntries.length - this.MAX_CACHE_SIZE);

        toRemove.forEach(({ key }) => localStorage.removeItem(key));
        console.log(`[LogoCache] Removed ${toRemove.length} oldest entries to enforce size limit`);
      }
    } catch (error) {
      console.error(`[LogoCache] Error enforcing storage limit:`, error);
    }
  }

  // Public method to clear all cached data (for debugging/admin)
  public clearAllCache(): void {
    try {
      // Clear memory cache
      this.memoryCache.clear();

      // Clear localStorage cache
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => localStorage.removeItem(key));
      console.log(`[LogoCache] Cleared all cache data (${keysToRemove.length} entries)`);
    } catch (error) {
      console.error(`[LogoCache] Error clearing cache:`, error);
    }
  }

  // Public method to get cache statistics
  public getCacheStats(): { memorySize: number; localStorageSize: number; totalEntries: number } {
    let localStorageSize = 0;

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_KEY_PREFIX)) {
          localStorageSize++;
        }
      }
    } catch (error) {
      console.error(`[LogoCache] Error getting cache stats:`, error);
    }

    return {
      memorySize: this.memoryCache.size,
      localStorageSize,
      totalEntries: localStorageSize,
    };
  }
}

// Create singleton instance
export const logoCache = new LogoCache();

// Export for testing and debugging
export { LogoCache };
